const CA_POSTAL_REGEX = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS_REGEX = /^\+?\d[\d\s()-]{9,}$/;
const URL_DOMAIN_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/;

function stripNonDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function isDigitsOfLength(value: string, min: number, max?: number): boolean {
  const digits = stripNonDigits(value);
  return digits.length >= min && digits.length <= (max ?? min);
}

export function isValidPostalCode(value: string, countryCode: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (countryCode === "US") {
    return US_ZIP_REGEX.test(trimmed);
  }
  if (countryCode === "CA") {
    return CA_POSTAL_REGEX.test(trimmed);
  }
  return trimmed.length > 0;
}

export function isValidPhone(value: string): boolean {
  const digitsOnly = stripNonDigits(value);
  return PHONE_DIGITS_REGEX.test(value.trim()) && digitsOnly.length >= 10;
}

export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

export function isValidUrl(value: string): boolean {
  const stripped = value.trim().replace(/^https?:\/\//, "");
  return URL_DOMAIN_REGEX.test(stripped);
}

/** SIN and SSN share the same format: exactly 9 digits. */
export function isValidTaxId(value: string): boolean {
  return isDigitsOfLength(value, 9);
}

export function isValidTransitNumber(value: string): boolean {
  return isDigitsOfLength(value, 5);
}

export function isValidInstitutionNumber(value: string): boolean {
  return isDigitsOfLength(value, 3);
}

/** US ABA routing number: exactly 9 digits. */
export function isValidRoutingNumber(value: string): boolean {
  return isDigitsOfLength(value, 9);
}

export function isValidAccountNumber(value: string): boolean {
  return isDigitsOfLength(value, 7, 12);
}

function getAgeOnDate(date: Date, reference: Date): number {
  const age = reference.getFullYear() - date.getFullYear();
  const monthDiff = reference.getMonth() - date.getMonth();
  const dayDiff = reference.getDate() - date.getDate();
  return monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
}

export type TDateOfBirthInvalidReason = "invalid_date" | "future" | "too_young" | "too_old" | null;

/** Returns the reason the DOB is invalid, or `null` when the date is valid. */
export function getDateOfBirthInvalidReason(date: Date | null): TDateOfBirthInvalidReason {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "invalid_date";
  }

  const today = new Date();
  if (date > today) {
    return "future";
  }

  const actualAge = getAgeOnDate(date, today);
  if (actualAge < 13) {
    return "too_young";
  }
  if (actualAge > 120) {
    return "too_old";
  }

  return null;
}

export function isValidDateOfBirth(date: Date | null): boolean {
  return getDateOfBirthInvalidReason(date) === null;
}

export const COMPANY_NAME_MAX_LENGTH = 100;
export const ADDRESS_MAX_LENGTH = 200;
